/**
 * The stretch of days a report covers: one day, one week or one month.
 *
 * Weeks start on SATURDAY, as the owner asked — Friday is the day off in
 * Somalia, so Saturday is the first working day of the week. Months are
 * calendar months. Every date is a YYYY-MM-DD day in the pharmacy's own
 * calendar; turning them into moments is the server's job, in its timezone.
 */

export const REPORT_PERIODS = ["day", "week", "month"] as const;
export type ReportPeriod = (typeof REPORT_PERIODS)[number];

/** 0 = Sunday … 6 = Saturday, as JavaScript counts. */
const WEEK_STARTS_ON = 6;

/** How long unsold before stock counts as dead. */
export const DEAD_STOCK_DAYS = 90;

/** How many medicines the Most sold list shows. */
export const MOST_SOLD_REPORT_COUNT = 10;

function toDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

function toIsoDay(at: Date): string {
  return at.toISOString().slice(0, 10);
}

/** First and last day, both included, of the period that holds `date`. */
export function periodRange(period: ReportPeriod, date: string): { from: string; to: string } {
  const at = toDate(date);
  if (period === "day") return { from: date, to: date };

  if (period === "week") {
    const back = (at.getUTCDay() - WEEK_STARTS_ON + 7) % 7;
    const from = new Date(at);
    from.setUTCDate(at.getUTCDate() - back);
    const to = new Date(from);
    to.setUTCDate(from.getUTCDate() + 6);
    return { from: toIsoDay(from), to: toIsoDay(to) };
  }

  const from = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), 1));
  const to = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + 1, 0));
  return { from: toIsoDay(from), to: toIsoDay(to) };
}

/** The longest stretch one report may cover: two years. */
export const REPORT_RANGE_MAX_DAYS = 731;

/** Days from `from` to `to`, both included. */
export function daysInRange(from: string, to: string): number {
  return Math.round((toDate(to).getTime() - toDate(from).getTime()) / 86_400_000) + 1;
}

/**
 * The days a report covers, asked either way: a period holding a date
 * ("this week"), or any From and To ("Choose dates"). From and To win when
 * given. Checked by the contract before it gets here.
 */
export function reportRange(asked: {
  period?: ReportPeriod;
  date?: string;
  from?: string;
  to?: string;
}): { from: string; to: string } {
  if (asked.from && asked.to) return { from: asked.from, to: asked.to };
  if (asked.period && asked.date) return periodRange(asked.period, asked.date);
  throw new Error("A report needs a period and a date, or from and to");
}

/** The same period one step earlier (-1) or later (+1): the ‹ › arrows. */
export function shiftPeriod(period: ReportPeriod, date: string, step: number): string {
  const at = toDate(date);
  if (period === "day") at.setUTCDate(at.getUTCDate() + step);
  else if (period === "week") at.setUTCDate(at.getUTCDate() + 7 * step);
  else return toIsoDay(new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + step, 1)));
  return toIsoDay(at);
}

/**
 * The key two medicines are counted together under in an all-branches
 * report: the same name and strength, spelt the same, and counted in the
 * same unit. Case and spacing do not matter.
 */
export function medicineKey(name: string, strength: string | null, baseUnit: string): string {
  const tidy = (v: string | null) => (v ?? "").trim().toLowerCase().replace(/\s+/g, " ");
  return `${tidy(name)}|${tidy(strength)}|${tidy(baseUnit)}`;
}
